import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Building2, Mail, MapPin, FileText } from "lucide-react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { safeLog } from '@/utils/safeLogger';

// Zod Schema
const billingDetailsSchema = z.object({
  billing_email: z.string().email("Please enter a valid email address"),
  address_line1: z.string().min(3, "Address must be at least 3 characters").max(200),
  address_line2: z.string().max(200).optional().nullable(),
  postal_code: z.string().min(4, "Postal code must be at least 4 characters").max(10),
  city: z.string().min(2, "City must be at least 2 characters").max(100),
  country: z.string().length(2, "Country code must be 2 characters"),
});

type BillingDetailsForm = z.infer<typeof billingDetailsSchema>;

// Country list (EU + major countries)
const COUNTRIES = [
  { code: "DE", name: "Germany" },
  { code: "AT", name: "Austria" },
  { code: "CH", name: "Switzerland" },
  { code: "FR", name: "France" },
  { code: "IT", name: "Italy" },
  { code: "ES", name: "Spain" },
  { code: "NL", name: "Netherlands" },
  { code: "BE", name: "Belgium" },
  { code: "PL", name: "Poland" },
  { code: "GB", name: "United Kingdom" },
  { code: "US", name: "United States" },
  { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" },
  { code: "SE", name: "Sweden" },
  { code: "DK", name: "Denmark" },
  { code: "NO", name: "Norway" },
  { code: "FI", name: "Finland" },
  { code: "IE", name: "Ireland" },
  { code: "PT", name: "Portugal" },
  { code: "CZ", name: "Czech Republic" },
];

export function BillingDetailsSettings() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [hasDetails, setHasDetails] = useState(false);

  const form = useForm<BillingDetailsForm>({
    resolver: zodResolver(billingDetailsSchema),
    defaultValues: {
      billing_email: user?.email || "",
      address_line1: "",
      address_line2: "",
      postal_code: "",
      city: "",
      country: "DE",
    },
  });

  // Load existing billing details
  const loadBillingDetails = useCallback(async () => {
    if (!user) return;
    
    const { data, error } = await supabase
      .from("billing_details")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();

    if (error) {
      console.error("Error loading billing details:", error);
      return;
    }

    if (data) {
      setHasDetails(true);
      form.reset({
        billing_email: data.billing_email,
        address_line1: data.address_line1,
        address_line2: data.address_line2 || "",
        postal_code: data.postal_code,
        city: data.city,
        country: data.country,
      });
    }
  }, [user, form]);

  useEffect(() => {
    loadBillingDetails();
  }, [loadBillingDetails]);

  const onSubmit = async (values: BillingDetailsForm) => {
    if (!user) return;
    setLoading(true);

    const { error } = await supabase
      .from("billing_details")
      .upsert({
        user_id: user.id,
        billing_email: values.billing_email,
        address_line1: values.address_line1,
        address_line2: values.address_line2 || null,
        postal_code: values.postal_code,
        city: values.city,
        country: values.country,
      }, { onConflict: "user_id" });

    setLoading(false);

    if (error) {
      toast.error("Failed to save billing details");
      console.error(error);
      return;
    }

    setHasDetails(true);
    toast.success("Billing details saved successfully");
  };

  return (
    <div className="rounded-xl border border-border/60 bg-card p-5 sm:p-6">
      <div className="flex items-start gap-3 mb-5">
        <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
          <Building2 className="w-5 h-5 text-muted-foreground" />
        </div>
        <div>
          <h3 className="font-semibold text-sm">Company & address</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {hasDetails ? "Your billing details are saved" : "No billing details saved yet"}
          </p>
        </div>
      </div>
      <div>
        <div className="mb-4 p-4 bg-muted/40 border border-border/60 rounded-lg">
          <p className="text-sm text-foreground">
            💡 <strong>Company name and VAT ID:</strong> You can add these during the Stripe checkout process.
          </p>
          <p className="text-sm text-foreground mt-2">
            💶 <strong>VAT:</strong> All prices are subject to 19% VAT.
          </p>
        </div>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Billing Email */}
            <FormField
              control={form.control}
              name="billing_email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Billing Email *
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="billing@company.com" {...field} />
                  </FormControl>
                  <FormMessage />
              </FormItem>
            )}
          />

          {/* Address Line 1 */}
            <FormField
              control={form.control}
              name="address_line1"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="flex items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    Street Address *
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="123 Main Street" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Address Line 2 */}
            <FormField
              control={form.control}
              name="address_line2"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Address Line 2 (optional)</FormLabel>
                  <FormControl>
                    <Input placeholder="Suite 400" {...field} value={field.value || ""} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              {/* Postal Code */}
              <FormField
                control={form.control}
                name="postal_code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Postal Code *</FormLabel>
                    <FormControl>
                      <Input placeholder="12345" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* City */}
              <FormField
                control={form.control}
                name="city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>City *</FormLabel>
                    <FormControl>
                      <Input placeholder="Berlin" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Country */}
            <FormField
              control={form.control}
              name="country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Country *</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" disabled={loading} className="w-full">
              {loading ? "Saving..." : hasDetails ? "Update Billing Details" : "Save Billing Details"}
            </Button>
          </form>
        </Form>
      </div>
    </div>
  );
}